import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, filter, map, Observable, throwError } from 'rxjs';
import { RoutesMapper } from '../../routes-mapper';

interface RequestOptions {
  params?: { [key: string]: any };
  body?: { [key: string]: any };
  headers?: { [key: string]: any };
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private router: Router
  ) { }

  private createHeaders(): HttpHeaders {
    let authorizationToken: string = localStorage.getItem('AccessToken') || '';
    let headers: { [key: string]: string } = {
      Authorization: `Bearer ${authorizationToken}`,
    };

    let httpHeaders = new HttpHeaders();
    if (headers) {
      for (const key in headers) {
        httpHeaders = httpHeaders.set(key, headers[key]);
      }
    }
    return httpHeaders;
  }

  private createParams(params?: { [key: string]: any }): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      for (const key in params) {
        httpParams = httpParams.set(key, params[key]);
      }
    }
    return httpParams;
  }

  replacePathInfo(route: string, pathInfo?: { [key: string]: any }): string {
    if (pathInfo) {
      const replaceableKeys = Object.keys(pathInfo);
      for (const key of replaceableKeys) {
        const str = '{' + key + '}';
        route = route.replace(str, pathInfo[key]);
      }
    }
    return route;
  }

  private handleError(
    error: HttpErrorResponse,
    preserveErrorResponse: boolean = false
  ) {
    let errorMessage: string = '';
    console.log('api error', error);

    errorMessage = error?.error?.message || error?.message || '';

    console.log(errorMessage);

    if (error.status === 401) {
      localStorage.clear();
      this.router?.navigate([RoutesMapper.LOGIN]);
    }

    if (preserveErrorResponse) {
      return throwError(() => error);
    }

    return throwError(() => errorMessage);
  }

  request<T>(
    method: string,
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication?: boolean,
    responseType: 'json' | 'blob' = 'json',
    preserveErrorResponse: boolean = false
  ): Observable<T> {
    let httpOptions: any = {
      body: options?.body,
      params: this.createParams(options?.params),
    };

    if (requiresAuthentication) {
      httpOptions['headers'] = this.createHeaders();
    }

    const req = new HttpRequest<T>(method, endpoint, httpOptions.body, {
      headers: httpOptions.headers,
      params: httpOptions.params,
      responseType: responseType
    });

    return this.http.request<T>(req).pipe(
      filter(
        (event): event is HttpResponse<T> => event instanceof HttpResponse
      ),
      map((response: HttpResponse<T>) => response.body as T),
      catchError((error: HttpErrorResponse) =>
        this.handleError(error, preserveErrorResponse)
      )
    );
  }

  get<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true,
    responseType: 'json' | 'blob' = 'json'
  ): Observable<T> {
    return this.request<T>(
      'GET',
      endpoint,
      options,
      requiresAuthentication,
      responseType
    );
  }

  post<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true,
    preserveErrorResponse: boolean = false
  ): Observable<T> {
    return this.request<T>(
      'POST',
      endpoint,
      options,
      requiresAuthentication,
      'json',
      preserveErrorResponse
    );
  }

  put<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true
  ): Observable<T> {
    return this.request<T>(
      'PUT',
      endpoint,
      options,
      requiresAuthentication
    );
  }

  delete<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true
  ): Observable<T> {
    return this.request<T>(
      'DELETE',
      endpoint,
      options,
      requiresAuthentication
    );
  }

  patch<T>(
    endpoint: string,
    options?: RequestOptions,
    requiresAuthentication: boolean = true
  ): Observable<T> {
    return this.request<T>(
      'PATCH',
      endpoint,
      options,
      requiresAuthentication
    );
  }
}